import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LockSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import useAdminLoad from "@/hooks/useAdminLoad";
import { useAdmin } from "@/admin/AuthContext";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api, mediaUrl } from "@/lib/api";
import { formatDate } from "@/lib/format";

const SAME_REVIEWER = "You banned this account, so another reviewer must decide this appeal.";
const OVERTURN_NOTE = "Restores the account. Removed chats and crushes are not restored.";
const TONE = { pending: "brand", upheld: "error", overturned: "success" };

function AppealCard({ a, canRole, onDecide }) {
  const u = a.user || {};
  const blocked = a.status === "pending" && canRole && !a.can_decide;
  return (
    <Panel className="p-6" data-testid={`appeal-card-${a.id}`}>
      <div className="flex flex-wrap items-start gap-4">
        <img src={mediaUrl(u.photo) || undefined} alt="" className="h-14 w-14 rounded-2xl bg-[var(--cw-surface-tertiary)] object-cover" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{u.first_name || "No name"}{u.age ? `, ${u.age}` : ""}</p>
            <Pill tone={TONE[a.status]} className="capitalize" data-testid={`appeal-status-${a.id}`}>{a.status}</Pill>
            {a.source && <Pill tone={a.source === "web" ? "brand" : "neutral"} data-testid={`appeal-source-${a.id}`}>{a.source === "web" ? "Web" : a.source === "app" ? "App" : a.source}</Pill>}
            {u.id && <Link to={`/admin/users/${u.id}`} className="cw-brand-deep rounded text-xs underline-offset-4 hover:underline" data-testid={`appeal-user-link-${a.id}`}>View user</Link>}
          </div>
          <p className="cw-soft mt-1 text-xs" data-testid={`appeal-ban-${a.id}`}>
            Banned for <strong className="font-medium text-foreground">{a.ban?.category_label}</strong> by {a.ban?.banned_by || "—"} · {formatDate(a.ban?.banned_at)}{a.ban?.note ? ` · “${a.ban.note}”` : ""}
          </p>
        </div>
        <span className="cw-muted text-xs">Appealed {formatDate(a.created_at)}</span>
      </div>
      <blockquote className="mt-4 whitespace-pre-line rounded-2xl bg-[var(--cw-surface-tertiary)] p-4 text-sm leading-6" data-testid={`appeal-text-${a.id}`}>{a.text}</blockquote>
      {a.status !== "pending" ? (
        <p className="cw-soft mt-4 text-sm" data-testid={`appeal-decision-${a.id}`}>Decided by {a.decided_by} · {formatDate(a.decided_at)}{a.decision_reason ? ` · “${a.decision_reason}”` : ""}</p>
      ) : canRole && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button variant="outline" disabled={!a.can_decide} onClick={() => onDecide(a, "upheld")} data-testid={`appeal-uphold-button-${a.id}`}>Uphold ban</Button>
          <Button disabled={!a.can_decide} onClick={() => onDecide(a, "overturned")} data-testid={`appeal-overturn-button-${a.id}`}>Overturn</Button>
          {blocked && <p className="cw-warning flex items-center gap-1.5 text-sm" data-testid={`appeal-same-reviewer-${a.id}`}><LockSimple size={14} />{SAME_REVIEWER} (Banned by {a.ban?.banned_by}.)</p>}
        </div>
      )}
    </Panel>
  );
}

export default function Appeals() {
  const { admin } = useAdmin();
  const canRole = admin?.role === "owner" || admin?.role === "moderator";
  const [status, setStatus] = useState("pending");
  const { data, error, loading, reload } = useAdminLoad(`/admin/appeals?status=${status}`);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");

  const decide = async () => {
    if (reason.trim().length < 3) throw new Error("A reason of at least 3 characters is required.");
    try {
      await api(`/admin/appeals/${pending.a.id}/decide`, { body: { decision: pending.decision, reason: reason.trim() } });
    } catch (e) {
      if (e.code === "same_reviewer") throw new Error(SAME_REVIEWER);
      if (e.code === "already_decided") { reload(); throw new Error("Someone already decided this appeal. The list has been refreshed."); }
      throw e;
    }
    toast.success(pending.decision === "overturned" ? "Appeal overturned. The account is restored." : "Ban upheld.");
    setReason("");
    reload();
  };
  const items = data?.items || [];
  const over = pending?.decision === "overturned";

  return (
    <div data-testid="admin-appeals-page">
      <PageHeader title="Appeals" description="One appeal per ban. Someone other than the admin who banned them decides." />
      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList>{["pending", "upheld", "overturned"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`appeals-tab-${s}`}>{s}</TabsTrigger>)}</TabsList>
      </Tabs>
      {!canRole && <Pill className="mb-4" data-testid="appeals-readonly-note">Read-only for your role</Pill>}
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <div className="space-y-4">
          {items.map((a) => <AppealCard key={a.id} a={a} canRole={canRole} onDecide={(aa, decision) => setPending({ a: aa, decision })} />)}
          {!items.length && <Panel><Empty title={`No ${status} appeals`} testId="appeals-empty" /></Panel>}
        </div>
      </LoadState>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setReason(""); } }} testId="appeal-decide-dialog" destructive={!over}
        title={over ? "Overturn this ban?" : "Uphold this ban?"} confirmLabel={over ? "Overturn" : "Uphold ban"} onConfirm={decide}
        description={over ? OVERTURN_NOTE : "The ban stays. They can't appeal again."}>
        <div><Label htmlFor="appeal-reason">Reason (required)</Label>
          <Textarea id="appeal-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2" data-testid="appeal-reason-input" /></div>
      </ConfirmDialog>
    </div>
  );
}
